import { Level, LevelType } from '../types/Level';
import { BatchOrder, Candidate, DraftLevel, DraftStatus, RejectedEntry } from './types';
import { buildPlanPrompt, parsePlan, PLAN_SCHEMA } from './planner';
import { Dropped, isCategoryType, selectCandidates, takenKeys } from './rules';
import { generateKeyLetters, keyLetterSource } from './keyLetters';
import { buildGrid } from './gridBuilder';

export const MAX_BATCH = 20;

export interface PipelineDeps {
  planJson: (prompt: string, schema: Record<string, unknown>) => Promise<unknown>;
  newId?: () => string;
  now?: () => number;
  random?: () => number;
}

export interface BatchContext {
  levels: Level[];
  rejected: RejectedEntry[];
  queued: DraftLevel[];
}

export interface BatchResult {
  drafts: DraftLevel[];
  dropped: Dropped[];
  shortfall: number;
}

const defaultId = () => `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const totalRequested = (order: BatchOrder): number =>
  (Object.keys(order.counts) as LevelType[]).reduce((sum, type) => sum + (order.counts[type] || 0), 0);

export const usesPicture = (type: LevelType): boolean => !isCategoryType(type);

// What a freshly planned draft still needs before it can be approved.
export const initialStatus = (type: LevelType): DraftStatus => {
  if (type === LevelType.CategoryWords) return 'ready';
  if (type === LevelType.WordSearch) return 'needs-grid';
  return 'needs-picture';
};

export const buildDraft = (candidate: Candidate, deps: PipelineDeps): DraftLevel => {
  const draft: DraftLevel = {
    id: (deps.newId || defaultId)(),
    levelType: candidate.levelType,
    status: initialStatus(candidate.levelType),
    difficulty: candidate.difficulty,
    flags: [],
    createdAt: (deps.now || Date.now)(),
  };

  if (candidate.context) draft.context = candidate.context;
  if (candidate.difficultyReason) draft.difficultyReason = candidate.difficultyReason;

  if (isCategoryType(candidate.levelType)) {
    draft.category = candidate.category;
    draft.words = candidate.words;
  } else {
    draft.sentence = candidate.sentence;
    if (candidate.levelType === LevelType.WhereIsThisLocated) draft.locationName = candidate.locationName;
  }

  const source = keyLetterSource(candidate);
  if (source !== null) {
    draft.keyLetters = generateKeyLetters(source, deps.random).letters;
  }

  if (candidate.levelType === LevelType.WordSearch) {
    const grid = buildGrid(candidate.words, deps.random);
    if (grid) {
      draft.wordSearchGrid = grid;
      draft.status = 'ready';
    }
  }

  return draft;
};

// One Claude call plans the batch; everything after it is free code.
export const runBatch = async (order: BatchOrder, context: BatchContext, deps: PipelineDeps): Promise<BatchResult> => {
  const total = totalRequested(order);
  if (total <= 0) throw new Error('Choose at least one level to generate.');
  if (total > MAX_BATCH) throw new Error(`A batch is limited to ${MAX_BATCH} levels.`);

  const prompt = buildPlanPrompt(
    order,
    [...context.levels, ...context.queued],
    context.rejected.map(entry => entry.label)
  );
  const candidates = parsePlan(await deps.planJson(prompt, PLAN_SCHEMA));

  const selection = selectCandidates(
    candidates,
    order.counts,
    takenKeys(context.levels, context.rejected, context.queued),
    candidate =>
      candidate.levelType === LevelType.WordSearch && !buildGrid(candidate.words, deps.random)
        ? 'Words do not fit together in the grid'
        : null
  );

  return {
    drafts: selection.accepted.map(candidate => buildDraft(candidate, deps)),
    dropped: selection.dropped,
    shortfall: selection.shortfall,
  };
};
